import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

export const fmtV = (n) => (n === 0 || !n ? (n === 0 ? "0.00" : "") : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const fmtA = (n) => (!n ? "" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const fmtQ = (n) => (!n ? "" : Number(n).toLocaleString("en-IN"));
export const fmtD = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return `${d.getDate()}-${d.toLocaleString("en-GB", { month: "short" })}-${d.getFullYear()}`;
  } catch { return iso; }
};

export function useEsc(back) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); back(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [back]);
}

// Keyboard row navigation — light highlight bar movable with Arrow keys, Enter opens.
export function useRowNav(count, onEnter) {
  const [sel, setSel] = useState(0);
  useEffect(() => {
    if (count && sel > count - 1) setSel(count - 1);
  }, [count, sel]);
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea") return;
      if (!count) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, count - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter" && onEnter) { e.preventDefault(); onEnter(sel); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [count, sel, onEnter]);
  return [sel, setSel];
}

// Row helpers: light beige bar on the selected row (auto on first row).
export const navRowCls = (active) => (active ? "bg-[#ece2c6]" : "hover:bg-[#f1e9d6]");
export const navRowProps = (i, sel, setSel) => ({
  onMouseEnter: () => setSel(i),
  ref: sel === i ? (el) => el && el.scrollIntoView({ block: "nearest" }) : undefined,
});

export function Shell({ title, subtitle, onBack, children, testid }) {
  const { user } = useAuth();
  const company = user?.company_name || "";
  return (
    <div className="w-full" data-testid={testid}>
      <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
        <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
          <span className="text-sm font-bold">{title}</span>
          <span className="text-sm font-black uppercase tracking-wide">{company}</span>
          <button onClick={onBack} data-testid={`${testid}-close`} className="p-1 mr-12 hover:bg-white/20 rounded-sm">
            <X size={16} weight="bold" />
          </button>
        </div>
        {subtitle && (
          <div className="flex justify-between px-4 py-1 text-sm font-bold text-slate-900">
            {subtitle}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function Head({ cols }) {
  return (
    <div className="flex items-end border-t border-b-2 border-slate-700 text-xs font-bold text-slate-800">
      {cols.map((c, i) => (
        <div key={i} className={`${c.w || "flex-1"} px-2 py-1 ${c.right ? "text-right" : ""} ${c.center ? "text-center" : ""}`}>{c.t}</div>
      ))}
    </div>
  );
}

export function TotalRow({ cells, testid }) {
  return (
    <div className="flex items-center border-t-2 border-slate-700 bg-[#f1e9d6] text-sm font-black text-slate-900" data-testid={testid}>
      {cells.map((c, i) => (
        <div key={i} className={`${c.w || "flex-1"} px-2 py-1.5 ${c.right ? "text-right" : ""}`}>{c.t}</div>
      ))}
    </div>
  );
}

export function SubMenu({ title, items, onPick, onBack, testid }) {
  const [sel, setSel] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onBack(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => (s + 1) % items.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => (s - 1 + items.length) % items.length); }
      else if (e.key === "Enter") {
        e.preventDefault(); e.stopPropagation();
        const it = items[sel];
        if (it) { if (it.key === "quit") onBack(); else onPick(it.key); }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [items, sel, onPick, onBack]);
  return (
    <div className="flex justify-center items-start pt-6 pb-10 min-h-[70vh]" data-testid={testid}>
      <div className="w-full max-w-md rounded-md overflow-hidden shadow-xl" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3" }}>
        <div className="text-white text-center py-2 text-sm font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>
          {title}
        </div>
        <div className="py-8 px-10 select-none">
          {items.map((it, idx) => (
            <div key={it.key} className={it.gapBefore ? "mt-5" : "mt-0.5"}>
              <button
                data-testid={`${testid}-${it.key}`}
                onMouseEnter={() => setSel(idx)}
                onClick={() => (it.key === "quit" ? onBack() : onPick(it.key))}
                className={`w-full text-left px-3 py-1 text-[15px] font-semibold rounded-sm transition-colors ${
                  sel === idx ? "bg-teal-600 text-white shadow-sm" : "text-slate-800 hover:bg-[#f1e9d6]"
                }`}
              >
                {it.label}
              </button>
            </div>
          ))}
        </div>
        <div className="px-4 py-1.5 text-[10px] font-semibold text-slate-500 text-center border-t border-[#e7dcc3]">
          ↑↓ Navigate · Enter Open · Esc Back
        </div>
        <div className="h-1.5" style={{ background: "#0f766e" }} />
      </div>
    </div>
  );
}
